// =============================================================================
// VendorReconcile — "Consumed vs Paid" per vendor.
//
// Consumed = what our logging captured (bottom-up, from ai_requests +
//            third_party_charges). Precise for metered/prepaid usage.
// Paid     = what we actually spent (top-down): ledger entries in the period,
//            or the prorated flat amount for subscriptions.
// Coverage = consumed / paid — how much of real spend our per-tenant tracking
//            actually sees. Low coverage = untracked usage (client-side Google,
//            unpriced models) OR purchases not recorded yet.
//
// Pure presentational — reuses the data the Runway board already loaded.
// =============================================================================

import { useMemo } from 'react';
import { Spinner } from '@/components/shared/UIComponents';
import type { VendorAccount, VendorLedgerEntry } from '@/lib/api';

const usd = (c: number) => '$' + (Math.round(c) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });

type Consumption = Map<string, { cents: number; events: number; unpriced: number }>;

interface Row {
  a: VendorAccount;
  consumed: number;
  paid: number;
  coverage: number | null;   // null = N/A (flat sub / no basis)
  unpriced: number;
}

export function VendorReconcile({ accounts, ledger, consumption, days, startIso, endIso, loading }: {
  accounts: VendorAccount[];
  ledger: VendorLedgerEntry[];
  consumption: Consumption;
  days: number;
  startIso: string;
  endIso: string;
  loading: boolean;
}) {
  const rows: Row[] = useMemo(() => {
    const s = new Date(startIso).getTime(), e = new Date(endIso).getTime();
    const paidByVendor = new Map<string, number>();
    for (const l of ledger) {
      const t = new Date(l.occurred_at).getTime();
      if (t >= s && t < e) paidByVendor.set(l.vendor_key, (paidByVendor.get(l.vendor_key) ?? 0) + (Number(l.amount_cents) || 0));
    }
    return accounts.filter(a => a.is_active).map(a => {
      const c = consumption.get(a.vendor_key);
      const consumed = c?.cents ?? 0;
      const ledgerPaid = paidByVendor.get(a.vendor_key) ?? 0;
      const isSub = a.billing_shape === 'subscription' || a.billing_shape === 'build';
      const subExpected = isSub && a.monthly_amount_cents ? Math.round(a.monthly_amount_cents * (days / 30)) : 0;
      const paid = ledgerPaid > 0 ? ledgerPaid : subExpected;
      // Coverage only makes sense for metered/prepaid (usage should ≈ spend).
      const coverage = !isSub && paid > 0 ? consumed / paid : null;
      return { a, consumed, paid, coverage, unpriced: c?.unpriced ?? 0 };
    }).sort((x, y) => (x.coverage ?? 2) - (y.coverage ?? 2)); // worst coverage first
  }, [accounts, ledger, consumption, days, startIso, endIso]);

  const totals = useMemo(() => {
    let consumed = 0, paid = 0, metConsumed = 0, metPaid = 0;
    for (const r of rows) {
      consumed += r.consumed; paid += r.paid;
      if (r.coverage != null) { metConsumed += r.consumed; metPaid += r.paid; }
    }
    return { consumed, paid, coverage: metPaid > 0 ? metConsumed / metPaid : null };
  }, [rows]);

  if (loading) return <div className="py-16 flex justify-center"><Spinner /></div>;

  const covCol = (c: number) => (c >= 0.85 ? 'text-emerald-600' : c >= 0.5 ? 'text-amber-600' : 'text-red-600');
  const covPct = (c: number) => Math.round(c * 100) + '%';

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-500 mb-1">Consumed (tracked) · {days}d</p><p className="text-xl font-medium text-surface-900">{usd(totals.consumed)}</p></div>
        <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-500 mb-1">Paid (actual)</p><p className="text-xl font-medium text-surface-900">{usd(totals.paid)}</p></div>
        <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-500 mb-1">Metered coverage</p><p className={`text-xl font-medium ${totals.coverage == null ? 'text-surface-400' : covCol(totals.coverage)}`}>{totals.coverage == null ? '—' : covPct(totals.coverage)}</p></div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-surface-400 text-xs">
              <th className="text-left font-normal py-2 pr-3">Vendor</th>
              <th className="text-right font-normal py-2 px-2">Consumed</th>
              <th className="text-right font-normal py-2 px-2">Paid</th>
              <th className="text-right font-normal py-2 px-2">Coverage</th>
              <th className="text-left font-normal py-2 pl-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isSub = r.a.billing_shape === 'subscription' || r.a.billing_shape === 'build';
              let note = '';
              if (isSub) note = 'flat sub — not metered';
              else if (r.a.billing_shape === 'sandbox') note = 'not live';
              else if (r.paid === 0) note = 'record purchases to reconcile';
              else if (!r.a.is_tenant_attributable) note = 'client-side — largely untracked';
              else if (r.unpriced > 0) note = `${r.unpriced.toLocaleString()} calls unpriced ($0)`;
              else if (r.coverage != null && r.coverage < 0.85) note = 'usage < spend — logging gap';
              return (
                <tr key={r.a.vendor_key} className="border-t border-surface-100">
                  <td className="py-2 pr-3 text-surface-900 font-medium">{r.a.display_name}</td>
                  <td className="text-right py-2 px-2 text-surface-600 tabular-nums">{r.consumed ? usd(r.consumed) : '·'}</td>
                  <td className="text-right py-2 px-2 text-surface-600 tabular-nums">{r.paid ? usd(r.paid) : '·'}</td>
                  <td className={`text-right py-2 px-2 tabular-nums ${r.coverage == null ? 'text-surface-400' : covCol(r.coverage)}`}>{r.coverage == null ? '—' : covPct(r.coverage)}</td>
                  <td className="py-2 pl-3 text-xs text-surface-500">{note}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-surface-400 mt-3">
        Coverage under 100% means you paid for more than the tracker captured — usually client-side Google, models missing a pricing row, or purchases you haven't recorded. Subscriptions show no coverage because they're flat, not metered.
      </p>
    </div>
  );
}
